"use client";

import { TimeFormatPreference } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import moment from "moment";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

interface TimeFormatContextType {
  timeFormatMode: TimeFormatPreference;
  setTimeFormatMode: (mode: TimeFormatPreference) => void;
}

const TimeFormatContext =
  createContext<TimeFormatContextType | undefined>(undefined);

interface TimeFormatProviderProps {
  children: ReactNode;
}

export function TimeFormatProvider({ children }: TimeFormatProviderProps) {
  const { courier } = useAuth();
  const [timeFormatMode, setTimeFormatMode] = useState<TimeFormatPreference>(
    TimeFormatPreference.TwelveHour
  );

  useEffect(() => {
    if (courier?.courier?.timeFormatPreference) {
      setTimeFormatMode(courier.courier.timeFormatPreference);
    }
  }, [courier]);

  useEffect(() => {
    const is12 = timeFormatMode === TimeFormatPreference.TwelveHour;
    moment.updateLocale(moment.locale(), {
      longDateFormat: {
        L: "MM/DD/YYYY",
        LL: "MMMM Do YYYY",
        LLL: "MMMM Do YYYY LT",
        LLLL: "dddd, MMMM Do YYYY LT",
        LT: is12 ? "h:mm A" : "HH:mm",
        LTS: is12 ? "h:mm:ss A" : "HH:mm:ss",
      },
    });
  }, [timeFormatMode]);

  return (
    <TimeFormatContext.Provider value={{ timeFormatMode, setTimeFormatMode }}>
      {children}
    </TimeFormatContext.Provider>
  );
}

export function useTimeFormat() {
  const context = useContext(TimeFormatContext);
  if (context === undefined) {
    throw new Error("useTimeFormat must be used within a TimeFormatProvider");
  }
  return context;
}
